import { useState, useEffect, useCallback } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'mamf-theme'

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'dark'
  const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
  return stored ?? 'dark'
}

const applyTheme = (theme: Theme) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const color = theme === 'dark' ? '#0c0e0f' : '#ffffff'

  if (theme === 'dark') {
    root.classList.add('dark')
    root.classList.remove('light')
    root.setAttribute('data-theme', 'dark')
  } else {
    root.classList.remove('dark')
    root.classList.add('light')
    root.setAttribute('data-theme', 'light')
  }

  root.style.backgroundColor = color
  if (document.body) {
    document.body.style.backgroundColor = color
  }
}

const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    applyTheme(theme)

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
        setTheme(e.newValue)
        applyTheme(e.newValue)
      }
    }

    const handleCustomThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent<Theme>
      if (customEvent.detail) {
        setTheme(customEvent.detail)
        applyTheme(customEvent.detail)
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('mamf-theme-change', handleCustomThemeChange)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('mamf-theme-change', handleCustomThemeChange)
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light'
      localStorage.setItem(STORAGE_KEY, next)
      applyTheme(next)
      window.dispatchEvent(new CustomEvent('mamf-theme-change', { detail: next }))
      return next
    })
  }, [])

  return { theme, toggleTheme }
}

export default useTheme
